import type { SerializedConstructor } from '@langchain/core/load/serializable';
import { LinearProgress } from '@mui/joy';

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Error } from '@/components/Hub/Error';
import { EPromptType } from '@/types/schema';

import { useCommit, useRepo } from '../../hooks/useSwr';
import { PlaygroundMain } from '../Playground/PlaygroundHome/PlaygroundMain';
import { storedManifestToPlaygroundManifestAndOptions } from '../Playground/utils/Playground.utils';
import {
  emptyChatPromptManifest,
  emptyPromptManifest,
  emptyStructuredPromptManifest,
} from './HubPlayground.utils';

// this component is used when the user accesses the playground from the Prompts page OR the Public Hub
// Projects -> [click on a prompt] -> Try it button
export function HubPlayground() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const repo = useRepo(params.owner, params.repo ?? '');
  const commitHash =
    searchParams.get('commit') ||
    (repo.data?.repo.full_name === `${params.owner}/${params.repo}` ||
    (!params.owner && repo.data?.repo.repo_handle === params.repo)
      ? repo.data?.repo?.last_commit_hash
      : undefined) ||
    undefined;
  const commit = useCommit({
    owner: params.owner,
    repo: params.repo ?? '',
    sha: commitHash,
    get_examples: true,
  });

  const newManifestType = searchParams.get('type') as
    | EPromptType
    | 'dataset-eval'
    | 'session-eval'
    | undefined;

  const manifest: SerializedConstructor = useMemo(() => {
    return (
      commit.data?.manifest ??
      (newManifestType === EPromptType.CHAT
        ? emptyChatPromptManifest()
        : newManifestType === EPromptType.STRUCTURED
        ? emptyStructuredPromptManifest(undefined)
        : newManifestType === 'dataset-eval'
        ? emptyStructuredPromptManifest('dataset-eval')
        : newManifestType === 'session-eval'
        ? emptyStructuredPromptManifest('session-eval')
        : emptyPromptManifest())
    );
  }, [commit.data, newManifestType]);

  const error = repo?.error?.message || commit?.error?.message;
  if (error) {
    return <Error>{error}</Error>;
  }

  const loading = !(commitHash ? commit.data : repo.data);
  if (loading)
    return (
      <div>
        <LinearProgress />
      </div>
    );

  const { promptManifest, modelManifest, options, isChat } =
    storedManifestToPlaygroundManifestAndOptions(manifest);

  return manifest.id[1] === 'prompts' ||
    manifest.id.join('/') === 'langchain/schema/runnable/RunnableSequence' ? (
    <PlaygroundMain
      datasetId={searchParams.get('datasetId') ?? undefined}
      promptManifest={promptManifest}
      modelManifest={modelManifest}
      options={options}
      type={isChat ? EPromptType.CHAT : EPromptType.INSTRUCT}
      repo={repo.data?.repo}
      commitHash={commitHash}
      disabledCompare
    />
  ) : null;
}
